import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, CalendarClock, Users2, ChevronRight, X, CheckCircle2 } from "lucide-react";

export const MEETING_TYPES = [
  { key: "target_setting", label: "Target-setting", color: "#6C8EBF" },
  { key: "review", label: "Review", color: "#9A7AA0" },
  { key: "set_planning", label: "SET planning", color: "#5d8a73" },
  { key: "general", label: "General", color: "#7c8794" },
];
export const typeMeta = (k) => MEETING_TYPES.find((t) => t.key === k) || MEETING_TYPES[3];

const fmtWhen = (iso) => {
  if (!iso) return "No date set";
  try { return new Date(iso).toLocaleString("en-IE", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function Meetings() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => setMeetings([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const upcoming = (meetings || []).filter((m) => m.status !== "done" && (!m.when || new Date(m.when).getTime() >= now - 36e5));
  const past = (meetings || []).filter((m) => !upcoming.includes(m));

  return (
    <div data-testid="meetings-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-7">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-bold text-slate-800 flex items-center gap-2.5">
            <span className="h-10 w-10 rounded-2xl bg-[#6C8EBF]/15 text-[#4f6f9c] flex items-center justify-center"><CalendarClock size={22} /></span>
            Meetings
          </h1>
          <p className="text-sm text-slate-500 mt-1">Plan and coordinate pupil support meetings with colleagues who share the class.</p>
        </div>
        <button data-testid="new-meeting-btn" onClick={() => setOpen(true)}
          className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2 shrink-0">
          <Plus size={18} /> New meeting
        </button>
      </div>

      {meetings === null && (
        <div className="flex justify-center py-20 text-slate-300"><Loader2 className="animate-spin" /></div>
      )}
      {meetings !== null && meetings.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-16" data-testid="meetings-empty">No meetings yet. Schedule one to coordinate a pupil's support with your colleagues.</p>
      )}
      {meetings !== null && meetings.length > 0 && (
        <div className="space-y-8">
          <MeetingGroup title="Upcoming" rows={upcoming} navigate={navigate} />
          <MeetingGroup title="Past & completed" rows={past} navigate={navigate} muted />
        </div>
      )}

      {open && <NewMeetingModal onClose={() => setOpen(false)} onCreated={(id) => navigate(`/meetings/${id}`)} />}
    </div>
  );
}

function MeetingGroup({ title, rows, navigate, muted }) {
  if (!rows.length) return null;
  return (
    <section>
      <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{title}</h2>
      <div className="space-y-3">
        {rows.map((m) => {
          const t = typeMeta(m.type);
          return (
            <button key={m.id} data-testid={`meeting-card-${m.id}`} onClick={() => navigate(`/meetings/${m.id}`)}
              className={`w-full text-left bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-5 hover:shadow-[0_8px_30px_rgba(42,59,71,0.09)] hover:-translate-y-0.5 transition-all flex items-center gap-4 ${muted ? "opacity-75" : ""}`}>
              <span className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white" style={{ backgroundColor: t.color }}><CalendarClock size={20} /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-['Outfit'] font-semibold text-slate-800 truncate">{m.title}</p>
                  <span className="text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ color: t.color, backgroundColor: `${t.color}1a` }}>{t.label}</span>
                  {m.status === "done" && <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Done</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{m.student_name}{m.class_name ? ` · ${m.class_name}` : ""}</p>
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-3">
                  <span>{fmtWhen(m.when)}</span>
                  <span className="inline-flex items-center gap-1"><Users2 size={12} /> {(m.attendees || []).length}</span>
                </p>
              </div>
              <ChevronRight size={18} className="text-slate-300 shrink-0" />
            </button>
          );
        })}
      </div>
    </section>
  );
}

function NewMeetingModal({ onClose, onCreated }) {
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ student_id: "", title: "", type: "review", when: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/students").then((r) => setStudents(r.data)).catch(() => {}); }, []);

  const create = async () => {
    if (!form.student_id) { toast.error("Choose a pupil first."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/meetings", form);
      toast.success("Meeting created");
      onCreated(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 animate-rise" onClick={(e) => e.stopPropagation()} data-testid="new-meeting-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-['Outfit'] text-xl font-bold text-slate-800">New meeting</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <label className="block mb-3">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Pupil</span>
          <select data-testid="meeting-student-select" value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })}
            className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#6C8EBF] focus:bg-white">
            <option value="">Select a pupil…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="block mb-3">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Title</span>
          <input data-testid="meeting-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Term 1 review"
            className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#6C8EBF] focus:bg-white" />
        </label>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Type</span>
            <select data-testid="meeting-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#6C8EBF] focus:bg-white">
              {MEETING_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">When</span>
            <input type="datetime-local" data-testid="meeting-when-input" value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })}
              className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#6C8EBF] focus:bg-white" />
          </label>
        </div>
        <button data-testid="create-meeting-btn" onClick={create} disabled={saving}
          className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-2.5 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create meeting
        </button>
      </div>
    </div>
  );
}
